import { z } from "zod";
import { idSchema, mediaPath } from "../manifest/schema.js";

const hex = z.string().regex(/^#[0-9a-fA-F]{6}$/);

const option = { id: idSchema, name: z.string().min(1).max(40) };

/**
 * A car configurator: every combination of paint x wheels has an exterior image and every trim has an interior image.
 * Everything is "price on request" (no prices, no invented option names or specifications). See docs/ADDING-AREAS.md.
 */
export const configuratorSchema = z
  .object({
    schemaVersion: z.literal(1),
    id: idSchema,
    title: z.string().min(1),
    /** Product (price on request) that "Add to enquiry" puts in the basket. */
    productId: idSchema,
    /** Shown instead of a price. */
    priceLabel: z.string().min(1).default("Price on request"),
    /** Body size label for the basket line. */
    bodyLabel: z.string().min(1).default("Coupé"),
    /** `city`: where the exterior pictures for this paint are set (each paint is shown in a different city). */
    paints: z.array(z.object({ ...option, hex, city: z.string().min(1).max(40).optional() })).min(2).max(12),
    wheels: z.array(z.object({ ...option, description: z.string().min(1).max(120) })).min(2).max(8),
    /** `reveal`: optional clip (no extension, `.mp4` + `.webm`) of the host opening the door onto this interior; it ends on the interior image. */
    trims: z.array(z.object({ ...option, description: z.string().min(1).max(120), swatches: z.array(hex).min(1).max(2), reveal: mediaPath.optional() })).min(2).max(8),
    /** Media path templates (no extension), relative to the media base. `{paint}`, `{wheels}` and `{trim}` are option ids. */
    images: z.object({
      exterior: mediaPath.refine((t) => t.includes("{paint}") && t.includes("{wheels}"), "exterior template needs {paint} and {wheels}"),
      interior: mediaPath.refine((t) => t.includes("{trim}"), "interior template needs {trim}"),
      /** File extension of the images (without dot). */
      extension: z.enum(["webp", "jpg"]).default("webp")
    }),
    defaults: z.object({ paint: idSchema, wheels: idSchema, trim: idSchema })
  })
  .superRefine((c, ctx) => {
    const dupes = (kind: string, ids: string[]) => {
      const seen = new Set<string>();
      for (const id of ids) {
        if (seen.has(id)) ctx.addIssue({ code: "custom", message: `duplicate ${kind} id "${id}"` });
        seen.add(id);
      }
    };
    dupes("paint", c.paints.map((p) => p.id));
    dupes("wheels", c.wheels.map((p) => p.id));
    dupes("trim", c.trims.map((p) => p.id));
    if (!c.paints.some((p) => p.id === c.defaults.paint)) ctx.addIssue({ code: "custom", message: `defaults.paint "${c.defaults.paint}" is not a paint` });
    if (!c.wheels.some((p) => p.id === c.defaults.wheels)) ctx.addIssue({ code: "custom", message: `defaults.wheels "${c.defaults.wheels}" is not a wheel option` });
    if (!c.trims.some((p) => p.id === c.defaults.trim)) ctx.addIssue({ code: "custom", message: `defaults.trim "${c.defaults.trim}" is not a trim` });
  });

export type Configurator = z.infer<typeof configuratorSchema>;
